import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {SpProjectDto} from '../../../model/dto/entity/sp-project/sp-project.dto';
import {SpProjectService} from '../../../service/api/sp-project/sp-project.service';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {SpMcpService} from '../../../service/domain/sp-mcp/sp-mcp.service';
import {SpMcpBoardDetailDto} from '../../../model/dto/api/sp-mcp-board-detail/sp-mcp-board-detail.dto';
import {SpMcpBoardLaneDto} from '../../../model/dto/api/sp-mcp-board-lane/sp-mcp-board-lane.dto';
import {SpMcpBoardTicketDto} from '../../../model/dto/api/sp-mcp-board-ticket/sp-mcp-board-ticket.dto';
import {SpMcpProjectDetailDto} from '../../../model/dto/api/sp-mcp-project-detail/sp-mcp-project-detail.dto';
import {SpMcpSprintListItemDto} from '../../../model/dto/api/sp-mcp-sprint-list-item/sp-mcp-sprint-list-item.dto';
import {SpBoardService} from '../../../service/api/sp-board/sp-board.service';
import {ObjectRequestList} from '../../../../../core/shared/dto/object/request/object-request-list';
import {DtoList} from '../../../../../core/shared/dto/dto.list';
import {PagingDto} from '../../../../../core/shared/dto/model/paging.dto';

@Component({
    selector: 'mvs-sp-project-object-component',
    templateUrl: './sp-project-object.component.html',
    styleUrls: ['./sp-project-object.component.scss'],
    standalone: false
})
export class SpProjectObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: Runtime
    * Reason: Project detail data widget for displaying single project overview
    */
    projectDataWidget: WidgetData;

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: Runtime
    * Reason: Board table widget filtered by project for showing project boards
    */
    boardWidget: WidgetData;

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: Runtime
    * Reason: Sprint table widget filtered by project for showing project sprints
    */
    sprintWidget: WidgetData;

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: Runtime
    * Reason: Project ticket table widget filtered by project for managing tickets
    */
    ticketWidget: WidgetData;

    activeNavItem: string = 'overview';
    projectDto: SpProjectDto;

    // Board/Kanban (MCP-based)
    boardDetail: SpMcpBoardDetailDto;
    mcpLanes: SpMcpBoardLaneDto[] = [];
    mcpTickets: SpMcpBoardTicketDto[] = [];
    kanbanLoading: boolean = false;
    activeBoardId: number;

    // MCP project detail for sprint actions
    mcpProjectDetail: SpMcpProjectDetailDto;
    sprints: SpMcpSprintListItemDto[] = [];
    selectedSprint: SpMcpSprintListItemDto;

    // Add Ticket Dialog
    showAddTicketDialog: boolean = false;
    ticketSearchTerm: string = '';
    ticketSearchResults: any[] = [];
    ticketSearchLoading: boolean = false;
    selectedTicketId: number;
    addingTicket: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        protected navigationService: MvsObjectNavigationService,
        protected projectService: SpProjectService,
        protected boardService: SpBoardService,
        protected spMcpService: SpMcpService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    onObjectChanged() {
        this.setupNavigationItems();
        this.refreshComponent();
    }

    refreshComponent() {
        if (!this.objectIdentifier) {
            return;
        }

        this.loadProjectData();
        this.loadOverviewWidgets();
        this.loadMcpProjectDetail();
        this.initialized = true;
    }

    setupNavigationItems() {
        const navItems: NavigationItem[] = [
            {
                label: 'Overview',
                action: 'overview',
                tooltip: 'Project Overview',
                icon: 'fa-regular fa-house',
                toggleable: false,
                default: true
            },
            {
                label: 'Board',
                action: 'board',
                tooltip: 'Kanban Board',
                icon: 'fa-regular fa-table-columns',
                toggleable: false,
                default: false
            },
            {
                label: 'Sprints',
                action: 'sprints',
                tooltip: 'Sprints',
                icon: 'fa-regular fa-forward',
                toggleable: false,
                default: false
            },
            {
                label: 'Tickets',
                action: 'tickets',
                tooltip: 'Project Tickets',
                icon: 'fa-regular fa-ticket',
                toggleable: false,
                default: false
            }
        ];

        this.onNavigationItems.emit(navItems);
    }

    handleNavigationItemChange(item: NavigationItem) {
        this.activeNavItem = item.action;

        if (item.action === 'board') {
            this.loadKanbanData();
        }
        if (item.action === 'sprints') {
            this.loadMcpProjectDetail();
        }
    }

    loadProjectData() {
        this.projectService.get(this.objectIdentifier.objectId).subscribe({
            next: (dto: SpProjectDto) => {
                this.projectDto = dto;
                this.onPageTitle.emit(dto.name || 'Project');
            }
        });
    }

    loadMcpProjectDetail() {
        this.spMcpService.getProjectDetail(this.objectIdentifier.objectId).subscribe({
            next: (detail: SpMcpProjectDetailDto) => {
                this.mcpProjectDetail = detail;
                this.sprints = detail.sprints || [];
            }
        });
    }

    loadOverviewWidgets() {
        const projectId = this.objectIdentifier.objectId;
        const projectFilter = [
            FilterCriteria.create('e.projectId', FilterCriteria.cOperatorEqual, projectId)
        ];

        /**
        * AI:
        * Status: 'in progress'
        * Type: Widget
        * SubType: Runtime
        * Reason: Project detail data widget for displaying single project overview
        */
        this.projectDataWidget = WidgetFactory.createWidgetEntityData(
            'sp.project.object.detail.data',
            'Project Details',
            'sp.SpProject',
            this.objectIdentifier.objectId
        );

        /**
        * AI:
        * Status: 'in progress'
        * Type: Widget
        * SubType: Runtime
        * Reason: Board table widget filtered by project for showing project boards
        */
        this.boardWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sp.project.object.board.table',
            'Boards',
            'sp.SpBoard',
            projectFilter
        );

        const boardPrefillFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'projectDtoId', fieldValue: projectId}
        ];
        this.boardWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(boardPrefillFields);

        /**
        * AI:
        * Status: 'in progress'
        * Type: Widget
        * SubType: Runtime
        * Reason: Sprint table widget filtered by project for showing project sprints
        */
        this.sprintWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sp.project.object.sprint.table',
            'Sprints',
            'sp.SpSprint',
            projectFilter,
            [new Sorting('e.startDate', false)]
        );

        const sprintPrefillFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'projectDtoId', fieldValue: projectId}
        ];
        this.sprintWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(sprintPrefillFields);

        /**
        * AI:
        * Status: 'in progress'
        * Type: Widget
        * SubType: Runtime
        * Reason: Project ticket table widget filtered by project for managing tickets
        */
        this.ticketWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sp.project.object.ticket.table',
            'Project Tickets',
            'sp.SpProjectTicket',
            projectFilter,
            [new Sorting('e.priority', true)]
        );

        const ticketPrefillFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'projectDtoId', fieldValue: projectId}
        ];
        this.ticketWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(ticketPrefillFields);
    }

    // -------------------------------------------------------------------------
    // Board / Kanban (MCP-based)
    // -------------------------------------------------------------------------

    loadKanbanData() {
        if (!this.objectIdentifier) {
            return;
        }

        this.kanbanLoading = true;
        const projectId = this.objectIdentifier.objectId;

        const boardRequest = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('projectDtoId', FilterCriteria.cOperatorEqual, projectId)],
            []
        );

        this.boardService.list(boardRequest).subscribe({
            next: (result: DtoList) => {
                if (result?.entries?.length > 0) {
                    const board = result.entries[0];
                    this.activeBoardId = board.id;
                    this.loadBoardViaMcp(board.id);
                } else {
                    this.kanbanLoading = false;
                    this.mcpLanes = [];
                    this.mcpTickets = [];
                }
            },
            error: () => {
                this.kanbanLoading = false;
            }
        });
    }

    loadBoardViaMcp(boardId: number) {
        this.spMcpService.getBoardDetail(boardId).subscribe({
            next: (detail: SpMcpBoardDetailDto) => {
                this.boardDetail = detail;
                this.mcpLanes = (detail.lanes || []).sort((a, b) => (a.laneOrder || 0) - (b.laneOrder || 0));
                this.mcpTickets = detail.tickets || [];
                this.kanbanLoading = false;
            },
            error: () => {
                this.kanbanLoading = false;
            }
        });
    }

    handleTicketMoved(event: {projectTicketId: number; targetLaneId: number}) {
        this.spMcpService.moveTicket(event.projectTicketId, {targetLaneId: event.targetLaneId}).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Ticket', 'Ticket moved successfully.');
                this.loadKanbanData();
            },
            error: () => {
                this.messageService.showErrorMessage('Ticket', 'Failed to move ticket.');
            }
        });
    }

    handleTicketClick(ticket: SpMcpBoardTicketDto) {
        if (!ticket.ticketId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'tm.Ticket', ticket.ticketId, null, 'Ticket', null, null,
            MvsObjectNavigationActionEnum.any, 'side'
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    // -------------------------------------------------------------------------
    // Board Select (from Overview tab)
    // -------------------------------------------------------------------------

    handleBoardSelect(event: ObjectIdentifier | ObjectIdentifierData) {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            event.objectType, event.objectId, 'object', '', '', '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.openObjectInNewTab(entry);
    }

    // -------------------------------------------------------------------------
    // Sprint Management
    // -------------------------------------------------------------------------

    handleStartSprint(sprint: SpMcpSprintListItemDto) {
        this.spMcpService.startSprint(sprint.id).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Sprint', `Sprint "${sprint.name}" started.`);
                this.loadMcpProjectDetail();
            },
            error: () => {
                this.messageService.showErrorMessage('Sprint', 'Failed to start sprint.');
            }
        });
    }

    handleCompleteSprint(sprint: SpMcpSprintListItemDto) {
        this.spMcpService.completeSprint(sprint.id).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Sprint', `Sprint "${sprint.name}" completed.`);
                this.loadMcpProjectDetail();
            },
            error: () => {
                this.messageService.showErrorMessage('Sprint', 'Failed to complete sprint.');
            }
        });
    }

    getSprintStatusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        if (!status) return 'info';
        switch (status.toLowerCase()) {
            case 'planned':
                return 'secondary';
            case 'active':
                return 'info';
            case 'completed':
                return 'success';
            case 'cancelled':
                return 'danger';
            default:
                return 'info';
        }
    }

    // -------------------------------------------------------------------------
    // Add Ticket to Project Dialog
    // -------------------------------------------------------------------------

    openAddTicketDialog() {
        this.showAddTicketDialog = true;
        this.ticketSearchTerm = '';
        this.ticketSearchResults = [];
        this.selectedTicketId = null;
    }

    searchTickets() {
        if (!this.ticketSearchTerm || this.ticketSearchTerm.trim().length < 2) {
            this.ticketSearchResults = [];
            return;
        }

        this.ticketSearchLoading = true;
        const ticketService = this.coreService.getCrudService('tm.Ticket');
        if (!ticketService) {
            this.ticketSearchLoading = false;
            return;
        }

        const filters = [
            FilterCriteria.create('e.name', FilterCriteria.cOperatorContainsPattern, this.ticketSearchTerm.trim())
        ];

        const request = ObjectRequestList.createWithPaging(false, filters, [], PagingDto.createSize(20));

        ticketService.list(request).subscribe({
            next: (result: DtoList) => {
                this.ticketSearchResults = result?.entries || [];
                this.ticketSearchLoading = false;
            },
            error: () => {
                this.ticketSearchResults = [];
                this.ticketSearchLoading = false;
            }
        });
    }

    addSelectedTicketToProject() {
        if (!this.selectedTicketId) {
            return;
        }

        this.addingTicket = true;
        const projectId = this.objectIdentifier.objectId;

        this.spMcpService.addTicketToProject(projectId, {ticketId: this.selectedTicketId}).subscribe({
            next: () => {
                this.messageService.showSuccessMessage('Ticket', 'Ticket added to project.');
                this.showAddTicketDialog = false;
                this.addingTicket = false;
                this.selectedTicketId = null;
                this.loadOverviewWidgets();
                if (this.activeNavItem === 'board') {
                    this.loadKanbanData();
                }
            },
            error: () => {
                this.messageService.showErrorMessage('Ticket', 'Failed to add ticket to project.');
                this.addingTicket = false;
            }
        });
    }

    // -------------------------------------------------------------------------
    // Project Status
    // -------------------------------------------------------------------------

    getProjectStatusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        if (!this.projectDto) {
            return 'info';
        }
        switch (this.projectDto.status) {
            case 1:
                return 'success';
            case 0:
                return 'secondary';
            case 2:
                return 'warn';
            case 3:
                return 'success';
            case 4:
                return 'danger';
            default:
                return 'info';
        }
    }

    getProjectStatusLabel(): string {
        if (!this.projectDto) {
            return '';
        }
        switch (this.projectDto.status) {
            case 0:
                return 'Draft';
            case 1:
                return 'Active';
            case 2:
                return 'On Hold';
            case 3:
                return 'Completed';
            case 4:
                return 'Cancelled';
            default:
                return 'Unknown';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['objectIdentifier']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
